/**
 * Query Key Factory System
 *
 * Simple query key generation for consistent caching.
 * Will be expanded as app requirements become clearer.
 */

// Basic factory keys
export const factoryKeys = {
  all: ['factories'] as const,
  lists: () => [...factoryKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>): readonly unknown[] =>
    filters ? ([...factoryKeys.lists(), filters] as const) : factoryKeys.lists(),
  details: () => [...factoryKeys.all, 'detail'] as const,
  detail: (factoryId: string) => [...factoryKeys.details(), factoryId] as const,
};

// Asset keys (factory-scoped)
export const assetKeys = {
  all: (factoryId: string) => ['assets', factoryId] as const,
  lists: (factoryId: string) => [...assetKeys.all(factoryId), 'list'] as const,
  list: (factoryId: string, filters?: Record<string, unknown>): readonly unknown[] =>
    filters ? ([...assetKeys.lists(factoryId), filters] as const) : assetKeys.lists(factoryId),
  details: (factoryId: string) => [...assetKeys.all(factoryId), 'detail'] as const,
  detail: (factoryId: string, assetId: string) =>
    [...assetKeys.details(factoryId), assetId] as const,
  counts: (factoryId: string) => [...assetKeys.all(factoryId), 'counts'] as const,
};

// Position keys (factory-scoped)
export const positionKeys = {
  all: (factoryId: string) => ['positions', factoryId] as const,
  realtime: (factoryId: string) => [...positionKeys.all(factoryId), 'realtime'] as const,
  byAsset: (factoryId: string, assetId: string) =>
    [...positionKeys.all(factoryId), 'asset', assetId] as const,
  analytics: (
    factoryId: string,
    assetId?: string,
    startTime?: string,
    endTime?: string,
    assetType?: string
  ): readonly (string | undefined)[] =>
    assetId
      ? ([
          ...positionKeys.all(factoryId),
          'analytics',
          assetId,
          startTime,
          endTime,
          assetType,
        ] as const)
      : ([...positionKeys.all(factoryId), 'analytics', startTime, endTime, assetType] as const),
};

// Alert keys (factory-scoped)
export const alertKeys = {
  all: (factoryId: string) => ['alerts', factoryId] as const,
  lists: (factoryId: string) => [...alertKeys.all(factoryId), 'list'] as const,
  list: (factoryId: string, filters?: Record<string, unknown>): readonly unknown[] =>
    filters ? ([...alertKeys.lists(factoryId), filters] as const) : alertKeys.lists(factoryId),
  summary: (factoryId: string) => [...alertKeys.all(factoryId), 'summary'] as const,
  details: (factoryId: string) => [...alertKeys.all(factoryId), 'detail'] as const,
  detail: (factoryId: string, alertId: string) =>
    [...alertKeys.details(factoryId), alertId] as const,
};

// Zone keys (factory-scoped)
export const zoneKeys = {
  all: (factoryId: string) => ['zones', factoryId] as const,
  lists: (factoryId: string) => [...zoneKeys.all(factoryId), 'list'] as const,
  list: (factoryId: string, filters?: Record<string, unknown>): readonly unknown[] =>
    filters ? ([...zoneKeys.lists(factoryId), filters] as const) : zoneKeys.lists(factoryId),
  details: (factoryId: string) => [...zoneKeys.all(factoryId), 'detail'] as const,
  detail: (factoryId: string, zoneId: string) => [...zoneKeys.details(factoryId), zoneId] as const,
  dwellStats: (
    factoryId: string,
    startTime: string,
    endTime: string,
    assetIds: readonly string[] | null
  ) => [...zoneKeys.all(factoryId), 'dwell-stats', startTime, endTime, assetIds] as const,
};

// Cycle keys (factory-scoped)
export const cycleKeys = {
  all: (factoryId: string) => ['cycles', factoryId] as const,
  lists: (factoryId: string) => [...cycleKeys.all(factoryId), 'list'] as const,
  list: (factoryId: string, filters?: Record<string, unknown>): readonly unknown[] =>
    filters ? ([...cycleKeys.lists(factoryId), filters] as const) : cycleKeys.lists(factoryId),
  details: (factoryId: string) => [...cycleKeys.all(factoryId), 'detail'] as const,
  detail: (factoryId: string, cycleId: string) =>
    [...cycleKeys.details(factoryId), cycleId] as const,
  analytics: (
    factoryId: string,
    startTime: string,
    endTime: string,
    cycleId?: string,
    assetId?: string
  ) => [...cycleKeys.all(factoryId), 'analytics', startTime, endTime, cycleId, assetId] as const,
};

// Tag keys (factory-scoped) - placeholder for future
export const tagKeys = {
  all: (factoryId: string) => ['tags', factoryId] as const,
  lists: (factoryId: string) => [...tagKeys.all(factoryId), 'list'] as const,
  list: (factoryId: string, filters?: Record<string, unknown>): readonly unknown[] =>
    filters ? ([...tagKeys.lists(factoryId), filters] as const) : tagKeys.lists(factoryId),
  details: (factoryId: string) => [...tagKeys.all(factoryId), 'detail'] as const,
  detail: (factoryId: string, tagId: string) => [...tagKeys.details(factoryId), tagId] as const,
};

// Analytics keys (factory-scoped)
export const analyticsKeys = {
  all: (factoryId: string) => ['analytics', factoryId] as const,
  dashboard: (factoryId: string) => [...analyticsKeys.all(factoryId), 'dashboard'] as const,
  zoneUtilization: (factoryId: string, startTime: number, endTime: number) =>
    [...analyticsKeys.all(factoryId), 'zone-utilization', startTime, endTime] as const,
  topPerformers: (
    factoryId: string,
    metric: string,
    limit: number,
    startTime: number,
    endTime: number
  ) =>
    [...analyticsKeys.all(factoryId), 'top-performers', metric, limit, startTime, endTime] as const,
  timeSeries: (
    factoryId: string,
    metrics: string[],
    startTime: number,
    endTime: number,
    interval?: number
  ) =>
    [
      ...analyticsKeys.all(factoryId),
      'time-series',
      metrics,
      startTime,
      endTime,
      interval,
    ] as const,
  concurrency: (
    factoryId: string,
    assetType: string,
    startTime: number,
    endTime: number,
    slotMinutes: number
  ) =>
    [
      ...analyticsKeys.all(factoryId),
      'concurrency',
      assetType,
      startTime,
      endTime,
      slotMinutes,
    ] as const,
};

// User and tenant keys
export const userKeys = {
  all: ['user'] as const,
  current: () => [...userKeys.all, 'current'] as const,
  profile: (userId: string) => [...userKeys.all, 'profile', userId] as const,
  preferences: (userId: string) => [...userKeys.all, 'preferences', userId] as const,
  // User management keys
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>): readonly unknown[] =>
    filters ? ([...userKeys.lists(), filters] as const) : userKeys.lists(),
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (userId: string) => [...userKeys.details(), userId] as const,
};

export const tenantKeys = {
  all: ['tenant'] as const,
  info: (tenantId: string) => [...tenantKeys.all, 'info', tenantId] as const,
  settings: (tenantId: string) => [...tenantKeys.all, 'settings', tenantId] as const,
  factories: (tenantId: string) => [...tenantKeys.all, 'factories', tenantId] as const,
};

// Role keys (tenant-scoped)
export const roleKeys = {
  all: (tenantId: string) => ['roles', tenantId] as const,
  lists: (tenantId: string) => [...roleKeys.all(tenantId), 'list'] as const,
};

// Basic utility functions
export const queryKeyUtils = {
  // Extract factory ID from factory-scoped query keys
  extractFactoryId: (queryKey: readonly unknown[]): string | null => {
    if (queryKey.length >= 2 && typeof queryKey[1] === 'string') {
      return queryKey[1];
    }
    return null;
  },

  // Check if query key is factory-scoped
  isFactoryScoped: (queryKey: readonly unknown[]): boolean => {
    const factoryScopedTypes = [
      'assets',
      'positions',
      'alerts',
      'zones',
      'cycles',
      'tags',
      'analytics',
    ];
    return Array.isArray(queryKey) && factoryScopedTypes.includes(queryKey[0] as string);
  },
};

// Export all key factories
export const queryKeys = {
  factory: factoryKeys,
  assets: assetKeys,
  positions: positionKeys,
  alerts: alertKeys,
  zones: zoneKeys,
  cycles: cycleKeys,
  tags: tagKeys,
  analytics: analyticsKeys,
  user: userKeys,
  tenant: tenantKeys,
  roles: roleKeys,
  utils: queryKeyUtils,
};

// Convenience exports for direct access
export const QUERY_KEYS = {
  ZONE_UTILIZATION: analyticsKeys.zoneUtilization,
  TOP_PERFORMERS: analyticsKeys.topPerformers,
  TIME_SERIES_ANALYTICS: analyticsKeys.timeSeries,
  CONCURRENCY: analyticsKeys.concurrency,
};
